import React, { useState, useEffect } from 'react';
import { safeJson, networkMessage } from '../apiHelpers';
import PiPrice from '../components/PiPrice';
import { t } from '../i18n';
import { useNavigate } from 'react-router-dom';

const BACKEND = '/api';

const PLAN_COLORS = {
  free: '#6b7280',
  starter: '#0F6E56',
  pro: '#3b82f6',
  unlimited: '#3b82f6'
};

const PLAN_FEATURES = {
  free: ['5 Products', 'Basic listing', 'Pi payments', 'Reviews'],
  starter: ['50 Products', 'Analytics', 'Priority listing', 'Pi payments', 'Reviews', 'Boost available'],
  pro: ['100 Products', 'Advanced analytics', 'Top listing', 'Pi payments', 'Reviews', 'Cheaper boost', 'Featured badge'],
  unlimited: ['Unlimited Products', 'Full analytics', 'Top featured', 'Pi payments', 'Reviews', 'Free boosts', 'VIP badge', 'Priority support']
};

export default function MerchantTiers() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [upgrading, setUpgrading] = useState('');
  const [msg, setMsg] = useState('');
  const [currentPlan, setCurrentPlan] = useState('free');
  const [invoice, setInvoice] = useState(null);
  const navigate = useNavigate();
  const piUser = JSON.parse(localStorage.getItem('xpert_user') || '{}');

  useEffect(() => {
    fetchPlans();
    fetchCurrentPlan();
  }, []);

  const fetchPlans = async () => {
    try {
      const r = await fetch(BACKEND + '/merchants/pricing/');
      const d = await safeJson(r);
      setPlans(d.plans || []);
    } catch(e) {}
    setLoading(false);
  };

  const fetchCurrentPlan = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      const r = await fetch(BACKEND + '/merchants/profile/', {
        headers: { 'Authorization': 'Token ' + token }
      });
      const d = await safeJson(r);
      if (d.plan) setCurrentPlan(d.plan);
    } catch(e) {}
  };

  const upgradePlan = async (plan, pricepi) => {
    if (plan === 'free') return;
    if (!window.Pi) { alert(t('mt_open_pi')); return; }
    
    setUpgrading(plan);
    setMsg(t('mt_authing'));
    
    // Step 1: Authenticate first
    let auth;
    try {
      auth = await window.Pi.authenticate(['username', 'payments'], async function(payment) {
        const txid = payment.transaction ? payment.transaction.txid : null;
        if (!txid) return;
        await fetch('/api/merchants/payment/approve/', {
          method: 'POST', headers: {'Content-Type': 'application/json'},
          body: JSON.stringify({ paymentId: payment.identifier })
        });
        await fetch('/api/merchants/upgrade/', {
          method: 'POST', headers: {'Content-Type': 'application/json', 'Authorization': 'Token ' + localStorage.getItem('token')},
          body: JSON.stringify({ plan, paymentId: payment.identifier, txid })
        });
      });
    } catch(e) {
      setMsg('Auth error: ' + e.message);
      setUpgrading('');
      return;
    }

    // Step 2: Create payment
    setMsg(t('mt_starting'));
    try {
      await window.Pi.createPayment({
        amount: pricepi,
        memo: 'XpertCreation Merchant ' + plan + ' Plan - 30 days',
        metadata: { type: 'merchant_upgrade', plan }
      }, {
        onReadyForServerApproval: async function(pid) {
          setMsg(t('mt_approving'));
          try {
            await fetch('/api/merchants/payment/approve/', {
              method: 'POST', headers: {'Content-Type': 'application/json'},
              body: JSON.stringify({ paymentId: pid, amount: pricepi })
            });
          } catch(e) {}
        },
        onReadyForServerCompletion: async function(pid, txid) {
          try {
            const token = localStorage.getItem('token');
            const r = await fetch('/api/merchants/upgrade/', {
              method: 'POST',
              headers: {'Content-Type': 'application/json', 'Authorization': 'Token ' + token},
              body: JSON.stringify({ plan, paymentId: pid, txid })
            });
            const d = await safeJson(r);
            if (d.success) { setMsg(t('mt_upgraded') + plan + ' successfully!'); setCurrentPlan(plan); if (d.invoice) setInvoice(d.invoice); }
            else setMsg('Error: ' + d.error);
          } catch(e) {}
          setUpgrading('');
        },
        onCancel: function(pid) { 
          setMsg(t('mt_cancelled')); 
          setUpgrading(''); 
        },
        onError: async function(e, payment) { 
          if (payment) {
            const txid = payment.transaction ? payment.transaction.txid : null;
            if (txid) {
              try {
                await fetch('/api/merchants/payment/approve/', {
                  method: 'POST', headers: {'Content-Type': 'application/json'},
                  body: JSON.stringify({ paymentId: payment.identifier })
                });
                const token = localStorage.getItem('token');
                await fetch('/api/merchants/upgrade/', {
                  method: 'POST',
                  headers: {'Content-Type': 'application/json', 'Authorization': 'Token ' + token},
                  body: JSON.stringify({ plan, paymentId: payment.identifier, txid })
                });
                setMsg(t('mt_recovered'));
                setCurrentPlan(plan);
                setUpgrading('');
                return;
              } catch(err) {}
            }
          }
          setMsg('Error: ' + (e.message || e)); 
          setUpgrading(''); 
        }
      });
    } catch(e) { setMsg('Error: ' + e.message); setUpgrading(''); }
  };


  return (
    <div style={{ minHeight: '100vh', background: '#0a0e1a', color: '#fff', fontFamily: 'Arial, sans-serif' }}>
      {/* Header */}
      <div style={{ background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)', padding: '30px 20px', textAlign: 'center', borderBottom: '1px solid #2a2a4a' }}>
        <button onClick={() => navigate(-1)} style={{ position: 'absolute', left: 20, top: 30, background: 'none', border: 'none', color: '#0F6E56', cursor: 'pointer', fontSize: 14 }}>{t('mt_back')}</button>
        <h1 style={{ color: '#0F6E56', fontSize: 28, margin: '0 0 8px' }}>{t('mt_title')}</h1>
        <p style={{ color: '#aaa', margin: 0 }}>{t('mt_tag')}</p>
        {currentPlan && <div style={{ marginTop: 12 }}>
          <span style={{ background: PLAN_COLORS[currentPlan], color: '#fff', padding: '4px 16px', borderRadius: 20, fontSize: 13, fontWeight: 'bold' }}>
            Current: {currentPlan.toUpperCase()}
          </span>
        </div>}
      </div>

      <div style={{ maxWidth: 1000, margin: '0 auto', padding: 20 }}>
        {msg && <div style={{ background: msg.includes('success') || msg.includes('Upgraded') ? '#0d2a1a' : '#1a0d0d', border: '1px solid ' + (msg.includes('success') || msg.includes('Upgraded') ? '#22c55e' : '#ef4444'), borderRadius: 8, padding: 12, marginBottom: 20, color: msg.includes('success') || msg.includes('Upgraded') ? '#22c55e' : '#ef4444', textAlign: 'center' }}>{msg}</div>}

        {loading ? (
          <div style={{ textAlign: 'center', padding: 60, color: '#aaa' }}>{t('mt_loading')}</div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16 }}>
            {plans.map(plan => (
              <div key={plan.plan} style={{
                background: '#1a1a2e',
                border: '2px solid ' + (currentPlan === plan.plan ? PLAN_COLORS[plan.plan] : plan.plan === 'unlimited' ? '#3b82f6' : '#2a2a4a'),
                borderRadius: 16,
                padding: 24,
                position: 'relative',
                transform: plan.plan === 'pro' ? 'scale(1.02)' : 'none'
              }}>
                {plan.plan === 'pro' && (
                  <div style={{ position: 'absolute', top: -12, left: '50%', transform: 'translateX(-50%)', background: '#3b82f6', color: '#fff', padding: '3px 16px', borderRadius: 20, fontSize: 12, fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                    Most Popular
                  </div>
                )}
                {currentPlan === plan.plan && (
                  <div style={{ position: 'absolute', top: -12, right: 16, background: '#22c55e', color: '#000', padding: '3px 12px', borderRadius: 20, fontSize: 11, fontWeight: 'bold' }}>
                    Current
                  </div>
                )}

                <div style={{ textAlign: 'center', marginBottom: 20 }}>
                  <h2 style={{ color: PLAN_COLORS[plan.plan], fontSize: 22, margin: '0 0 8px', textTransform: 'capitalize' }}>{plan.plan}</h2>
                  <div style={{ fontSize: 36, fontWeight: 'bold', color: '#fff' }}>
                    {plan.price_pi === 0 ? 'Free' : plan.price_pi + ' Pi'}
                  </div>
                  {plan.price_pi > 0 && <div style={{ color: '#aaa', fontSize: 12 }}>{t('mt_per_month')}</div>}
                </div>

                <div style={{ marginBottom: 20 }}>
                  {(PLAN_FEATURES[plan.plan] || []).map((f, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', borderBottom: '1px solid #2a2a4a' }}>
                      <span style={{ color: '#22c55e', fontSize: 14 }}>✓</span>
                      <span style={{ color: '#aaa', fontSize: 13 }}>{f}</span>
                    </div>
                  ))}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0' }}>
                    <span style={{ color: '#0F6E56', fontSize: 14 }}>↑</span>
                    <span style={{ color: '#aaa', fontSize: 13 }}>Boost: <PiPrice pi={plan.boost_price_pi} /> / {plan.boost_days} days</span>
                  </div>
                </div>

                {plan.plan !== 'free' && currentPlan !== plan.plan ? (
                  <button onClick={() => upgradePlan(plan.plan, plan.price_pi)}
                    disabled={upgrading === plan.plan}
                    style={{ width: '100%', padding: 12, background: PLAN_COLORS[plan.plan], color: '#fff', border: 'none', borderRadius: 8, fontSize: 15, fontWeight: 'bold', cursor: 'pointer' }}>
                    {upgrading === plan.plan ? t('mt_processing') : t('mt_upgrade_to') + plan.plan.charAt(0).toUpperCase() + plan.plan.slice(1)}
                  </button>
                ) : currentPlan === plan.plan ? (
                  <div style={{ textAlign: 'center', padding: 12, color: '#22c55e', fontWeight: 'bold' }}>{t('mt_active')}</div>
                ) : (
                  <div style={{ textAlign: 'center', padding: 12, color: '#555', fontSize: 12 }}>{t('mt_lower')}</div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Boost Info */}
        <div style={{ background: '#1a1a2e', border: '1px solid #2a2a4a', borderRadius: 16, padding: 24, marginTop: 24 }}>
          <h3 style={{ color: '#0F6E56', margin: '0 0 12px' }}>{t('mt_boost')}</h3>
          <p style={{ color: '#aaa', fontSize: 14, margin: '0 0 12px' }}>
            Boost your products to appear at the top of search results and get more visibility!
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12 }}>
            {plans.filter(p => p.plan !== 'free').map(p => (
              <div key={p.plan} style={{ background: '#0d1b2a', borderRadius: 8, padding: 12, textAlign: 'center' }}>
                <div style={{ color: PLAN_COLORS[p.plan], fontWeight: 'bold', textTransform: 'capitalize', marginBottom: 4 }}>{p.plan}</div>
                <div style={{ color: '#fff', fontSize: 18, fontWeight: 'bold' }}><PiPrice pi={p.boost_price_pi} /></div>
                <div style={{ color: '#aaa', fontSize: 11 }}>{p.boost_days} days boost</div>
              </div>
            ))}
          </div>
        </div>
      </div>
      {invoice && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.85)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div style={{ background: '#fff', borderRadius: 16, padding: 32, maxWidth: 400, width: '100%', color: '#000' }}>
            <div style={{ textAlign: 'center', marginBottom: 20 }}>
              <div style={{ fontSize: 48, marginBottom: 8 }}>✅</div>
              <h2 style={{ color: '#22c55e', margin: 0 }}>{t('mt_success')}</h2>
            </div>
            <div style={{ background: '#f9fafb', borderRadius: 10, padding: 16, marginBottom: 16 }}>
              <h3 style={{ margin: '0 0 12px', color: '#1a1a2e', borderBottom: '2px solid #0F6E56', paddingBottom: 8 }}>{t('mt_invoice')}</h3>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <span style={{ color: '#666', fontSize: 13 }}>{t('mt_merchant')}</span>
                <span style={{ fontWeight: 'bold', fontSize: 13 }}>{invoice.merchant}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <span style={{ color: '#666', fontSize: 13 }}>{t('mt_plan')}</span>
                <span style={{ fontWeight: 'bold', fontSize: 13, textTransform: 'capitalize' }}>{invoice.plan}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <span style={{ color: '#666', fontSize: 13 }}>{t('mt_paid')}</span>
                <span style={{ fontWeight: 'bold', color: '#0F6E56', fontSize: 16 }}><PiPrice pi={invoice.amount} /></span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <span style={{ color: '#666', fontSize: 13 }}>{t('mt_date')}</span>
                <span style={{ fontSize: 13 }}>{invoice.date}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <span style={{ color: '#666', fontSize: 13 }}>{t('mt_valid')}</span>
                <span style={{ fontSize: 13, color: '#22c55e', fontWeight: 'bold' }}>{invoice.expires}</span>
              </div>
              {invoice.txid && <div style={{ marginTop: 8, padding: 8, background: '#e5e7eb', borderRadius: 6, wordBreak: 'break-all' }}>
                <span style={{ color: '#666', fontSize: 11 }}>TxID: {invoice.txid}</span>
              </div>}
            </div>
            <button onClick={() => setInvoice(null)}
              style={{ width: '100%', padding: 12, background: '#0F6E56', color: '#000', border: 'none', borderRadius: 8, fontWeight: 'bold', cursor: 'pointer', fontSize: 15 }}>
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
